import type { OS } from "../utils/pwaTwa/detectionHelper";
import { Box, Button, Modal, Typography, useTheme } from "@mui/material";
import { ReactComponent as CloseIcon } from "../assets/Global/Cancel25px.svg";
import translation from "../data/fa-IR.json";
import ImgBox24px from "./ImgBox24px";

const boxStyle = {
  position: "absolute" as const,
  inset: 0,
  bgcolor: "background.primary",
  p: "24px",
  display: "flex",
  flexDirection: "column" as const,
  alignItems: "center",
  justifyContent: "center",
  gap: "24px",
};

const boxStyleIos = {
  width: "250px",
  p: "8px",
  borderRadius: "8px",
  fontSize: "12px",
  bgcolor: "#fff",
  color: "#111213",
  border: "1px solid black",
  display: "flex",
  flexDirection: "row" as const,
  alignItems: "center",
  gap: "8px",
};

const buttonStyleAndroid = {
  bgcolor: "#92e82a",
  color: "#111213",
  width: "185px",
};

interface PwaModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  os: OS; // "android" | "ios" | "other"
}

function PwaModal({ open, setOpen, os }: PwaModalProps) {
  const theme = useTheme();
  const handleClose = () => setOpen(false);

  function handleBazarRedirect() {
    window.location.href =
      "http://cafebazaar.ir/app/?id=app.tfit.web.twa&ref=share";
  }

  function handleDirectInstall() {
    window.location.href = "https://cdn.tfit.app/app/TFIT.apk";
  }

  // Guard: only render UI for Android or iOS; others show nothing
  const isAndroid = os === "android";
  const isIOS = os === "ios";

  if (!isAndroid && !isIOS) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="pwa-modal-title"
      aria-describedby="pwa-modal-description"
    >
      <Box sx={boxStyle}>
        {/* CLOSE ICON */}
        <CloseIcon
          onClick={handleClose}
          style={{
            color: theme.palette.background.secondary,
            position: "absolute",
            top: "24px",
            right: "24px",
            cursor: "pointer",
          }}
        />

        {isAndroid ? (
          <>
            {/* LOGO */}
            <Box
              component="img"
              alt="Logo"
              src="/icons/Icon-maskable-192.png"
              sx={{ width: "192px", height: "192px", objectFit: "cover" }}
            />

            {/* TITLE */}
            <Typography
              variant="h1"
              component="h3"
              fontWeight={700}
              color="text.secondary"
            >
              {translation.Global.DownloadApp}
            </Typography>

            {/* ANDROID DIRECT LINK BUTTON */}
            <Button
              variant="contained"
              sx={buttonStyleAndroid}
              onClick={handleDirectInstall}
            >
              <ImgBox24px src="/icons/Android.png" alt="Android logo" />
              <Typography variant="caption" component="h4" fontWeight={600}>
                {translation.Global.GetDirectAndroid}
              </Typography>
            </Button>

            {/* BAZAR LINK BUTTON */}
            <Button
              variant="contained"
              sx={buttonStyleAndroid}
              onClick={handleBazarRedirect}
            >
              <ImgBox24px src="/icons/cafebazaar.png" alt="bazar logo" />
              <Typography variant="caption" component="h4" fontWeight={600}>
                {translation.Global.GetFromBazar}
              </Typography>
            </Button>
          </>
        ) : (
          <>
            {/* iOS */}
            <Box
              component="img"
              alt="Logo"
              src="/icons/Icon-maskable-192.png"
              sx={{ width: "120px", height: "120px", objectFit: "cover" }}
            />
            <Typography
              id="pwa-modal-title"
              variant="h3"
              component="h3"
              fontWeight={500}
              color="text.primary"
              textAlign="center"
            >
              {translation.Global.AddToHomeScreen}
            </Typography>

            <Box sx={boxStyleIos}>
              <ImgBox24px src="/icons/ShareIOS.png" alt="Share Icon" />
              <Typography variant="caption" component="h4" fontWeight={600}>
                انتخاب گزینه <strong>Share</strong> از نوار پایین
              </Typography>
            </Box>

            <Box sx={boxStyleIos}>
              <ImgBox24px src="/icons/addIOS.png" alt="ADD Icon" />
              <Typography variant="caption" component="h4" fontWeight={600}>
                انتخاب گزینه <strong>Add to homescreen</strong>
              </Typography>
            </Box>

            <Box sx={boxStyleIos}>
              <ImgBox24px src="/icons/Icon-192.png" alt="TFIT logo" />
              <Typography variant="caption" component="h4" fontWeight={600}>
                انتخاب گزینه <strong>Add</strong>
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
}

export default PwaModal;
